import {
  useQuery,
  useMutation,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import {
  fetchInteraction,
  updateInteraction,
  updateInteractionTranscript,
  deleteInteractionTranscript,
  fetchTranscriptSpeakers,
  renameTranscriptSpeaker,
  confirmTranscriptSpeakers,
  reassignTranscriptSpeaker,
  TranscriptChangedError,
  type TranscriptLineRef,
  fetchInteractionTypes,
  replaceInteractionTypes,
  InteractionType,
  InteractionTypeCreatePayload,
  searchEntitiesWith,
} from "../Dashboard/api";
import type { InteractionUpdatePayload } from "../Dashboard/types";
import useOrgId from "@/components/FpAppV4/hooks/useOrgId";

// Query keys for interactions
export const INTERACTION_QUERY_KEYS = {
  all: ["interactions"] as const,
  details: () => [...INTERACTION_QUERY_KEYS.all, "detail"] as const,
  detail: (id: string) => [...INTERACTION_QUERY_KEYS.details(), id] as const,
  types: (organizationId?: string) =>
    [...INTERACTION_QUERY_KEYS.all, "types", organizationId] as const,
  speakers: (id: string) =>
    [...INTERACTION_QUERY_KEYS.all, "speakers", id] as const,
  speakerRecords: (query: string, organizationId: string | undefined) =>
    [
      ...INTERACTION_QUERY_KEYS.all,
      "speaker-records",
      query,
      organizationId ?? null,
    ] as const,
};

// Hook to fetch a single interaction
export const useInteraction = (interactionId: string) => {
  return useQuery({
    queryKey: INTERACTION_QUERY_KEYS.detail(interactionId),
    queryFn: () => fetchInteraction(interactionId),
    enabled: !!interactionId,
    staleTime: 5 * 1000, // 5 seconds - much shorter to ensure fresh data
    refetchOnMount: true, // Refetch on mount if stale
  });
};

// Hook to update an interaction
export const useUpdateInteraction = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: InteractionUpdatePayload;
    }) => updateInteraction(id, payload),
    onSuccess: (data, variables) => {
      // Update the specific interaction in cache
      queryClient.setQueryData(
        INTERACTION_QUERY_KEYS.detail(variables.id),
        data
      );
    },
  });
};

// Hook to edit/trim a meeting transcript. The detail cache is invalidated so
// the page re-fetches the updated (or now-null) transcript.
export const useUpdateInteractionTranscript = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, transcript }: { id: string; transcript: string }) =>
      updateInteractionTranscript(id, transcript),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: INTERACTION_QUERY_KEYS.detail(variables.id),
      });
    },
  });
};

// The transcript's speakers and who they could be renamed to (#11884).
export const useTranscriptSpeakers = (
  interactionId: string,
  enabled: boolean
) =>
  useQuery({
    queryKey: INTERACTION_QUERY_KEYS.speakers(interactionId),
    queryFn: () => fetchTranscriptSpeakers(interactionId),
    enabled,
  });

// The organization's person records a transcript speaker could be, by name
// (#14412). A failed search is not retried: the picker keeps its last answer
// on screen itself, with the fixed list and the typed name. Each query has
// its own key, so a late answer for an older query never lands as a newer
// one's. Keyed by organization.
export const useSpeakerRecordSearch = (query: string, enabled: boolean) => {
  const organizationId = useOrgId();
  return useQuery({
    queryKey: INTERACTION_QUERY_KEYS.speakerRecords(query, organizationId),
    queryFn: () =>
      searchEntitiesWith({ searchTerm: query, entityTypes: ["person"] }),
    enabled,
    retry: false,
    staleTime: 30 * 1000,
  });
};

// Rename every line of one transcript speaker (#11884); to a person record,
// which the server tags as a participant, with `entityId` (#14412).
export const useRenameTranscriptSpeaker = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      label,
      name,
      userId,
      entityId,
    }: {
      id: string;
      label: string;
      name: string;
      userId?: string;
      entityId?: string;
    }) => renameTranscriptSpeaker(id, label, name, userId, entityId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: INTERACTION_QUERY_KEYS.detail(variables.id),
      });
      queryClient.invalidateQueries({
        queryKey: INTERACTION_QUERY_KEYS.speakers(variables.id),
      });
    },
  });
};

// Mark a transcript's speaker names as correct, all at once (#14225).
export const useConfirmTranscriptSpeakers = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => confirmTranscriptSpeakers(id),
    // The labels keep their names, so the text is unchanged: only the
    // speakers list is read again.
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({
        queryKey: INTERACTION_QUERY_KEYS.speakers(id),
      });
    },
  });
};

// Move transcript lines to another speaker (#13917).
export const useReassignTranscriptSpeaker = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      lines,
      speaker,
    }: {
      id: string;
      lines: TranscriptLineRef[];
      speaker: string | null;
    }) => reassignTranscriptSpeaker(id, lines, speaker),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: INTERACTION_QUERY_KEYS.detail(variables.id),
      });
      queryClient.invalidateQueries({
        queryKey: INTERACTION_QUERY_KEYS.speakers(variables.id),
      });
    },
    // The transcript changed under the reader: read it again, so the view
    // corrects itself rather than offering the stale line once more.
    onError: (error, variables) => {
      if (!(error instanceof TranscriptChangedError)) return;
      queryClient.invalidateQueries({
        queryKey: INTERACTION_QUERY_KEYS.detail(variables.id),
      });
      queryClient.invalidateQueries({
        queryKey: INTERACTION_QUERY_KEYS.speakers(variables.id),
      });
    },
  });
};

// Hook to remove a meeting transcript entirely (notes + AI summary preserved).
export const useDeleteInteractionTranscript = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (interactionId: string) =>
      deleteInteractionTranscript(interactionId),
    onSuccess: (_data, interactionId) => {
      queryClient.invalidateQueries({
        queryKey: INTERACTION_QUERY_KEYS.detail(interactionId),
      });
    },
  });
};

// Hook to fetch interaction types
export const useInteractionTypes = (
  organizationId?: string,
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: INTERACTION_QUERY_KEYS.types(organizationId),
    enabled: options?.enabled ?? true,
    queryFn: () => fetchInteractionTypes(organizationId),
    staleTime: 5 * 60 * 1000, // 5 minutes - these don't change often
  });
};

const INTERACTION_TYPES_STALE_TIME = 5 * 60 * 1000;

// Resolve the organization's interaction types outside of React, from cache
// when they are already there and by fetching when they are not.
//
// A new interaction's type defaults to one of these (#11350), and the create
// request cannot be sent without it. The hook alone is not enough there: a
// save that lands before the first response has no value to use and cannot
// wait for a re-render, so it awaits this instead. Rejects if the types
// genuinely cannot be loaded, which the caller must report rather than
// swallow.
export const ensureInteractionTypes = (
  queryClient: QueryClient,
  organizationId?: string,
  /** `retry: false` answers a failing read at once (a press that must not wait, #14547). */
  options: { retry?: false } = {}
): Promise<InteractionType[]> =>
  queryClient.ensureQueryData({
    queryKey: INTERACTION_QUERY_KEYS.types(organizationId),
    queryFn: () => fetchInteractionTypes(organizationId),
    staleTime: INTERACTION_TYPES_STALE_TIME,
    ...options,
  });

// The query families an interaction type save can change (#13041): RMS
// reads (bootstrap schema, views, search, counts), interaction details, and an
// entity page's interaction list. The types list itself is left out: the
// save's own answer has just been written into it.
const INTERACTION_TYPE_SAVE_AFFECTED_KEYS: ReadonlySet<string> = new Set([
  "rms",
  INTERACTION_QUERY_KEYS.all[0],
  "rmsEntityInteractions",
]);

const isAffectedByInteractionTypeSave = (
  queryKey: readonly unknown[]
): boolean => {
  const [family] = queryKey;
  if (typeof family !== "string") return false;
  if (!INTERACTION_TYPE_SAVE_AFFECTED_KEYS.has(family)) return false;
  const typesKey = INTERACTION_QUERY_KEYS.types();
  const isTypesList =
    queryKey.length === typesKey.length &&
    typesKey.every((part, index) => queryKey[index] === part);
  return !isTypesList;
};

// Hook to update interaction types
export const useUpdateInteractionTypes = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: InteractionTypeCreatePayload[]) =>
      replaceInteractionTypes(payload),
    onSuccess: (data) => {
      // Update the interaction types in cache
      queryClient.setQueryData(INTERACTION_QUERY_KEYS.types(), data);
      // A rename rewrites every interaction that carried the old name, the
      // bootstrap schema's type options and the saved views filtering on it
      // (#13041): refetch them so the new name shows everywhere without a
      // reload. Not awaited, so the save finishes without waiting on every
      // grid refetch.
      void queryClient.invalidateQueries({
        predicate: (query) => isAffectedByInteractionTypeSave(query.queryKey),
      });
    },
  });
};
